import { v4 as uuidv4 } from 'uuid';
import api, { getApiBaseUrl } from '@/services/api';
import { getAuthConfig } from '@/components/Auth/authProject';
import { getConsentState, readVisitorCookies } from '@/analytics/consentGate';
import type { DraftFields } from './draft';
import { frontDoorOn } from './flag';

/**
 * The landing capture: what a visitor sends from the landing box, and how far they get, recorded on our own server
 * (POST /api/front-door/events), never GA4 or Clarity. Only an example's click and a send are recorded; typing is
 * not. Nothing is stored in this browser for it: the page id lives in memory for this page load, which /landing,
 * /start and the home screen starting the video share, and the claim after sign-in matches on it and the words.
 */
const PAGE = uuidv4();

const visitor = () => ({ consent: getConsentState(), vid: readVisitorCookies().visitorId });

let open: Promise<boolean> | null = null;

/** Whether the server keeps what's sent here. One answer for the page load; no answer means no. The landing's notice shows on the same answer. */
export const captureOpen = (): Promise<boolean> => {
  if (!frontDoorOn()) return Promise.resolve(false);
  return (open ??= getAuthConfig().then((config) => config.capture));
};

async function beacon(event: Record<string, unknown>): Promise<void> {
  if (!(await captureOpen())) return;
  try {
    void fetch(`${getApiBaseUrl()}/front-door/events`, {
      method: 'POST',
      body: JSON.stringify({ ...event, page: PAGE }),
      keepalive: true,
      mode: 'cors',
      credentials: 'omit',
      headers: { 'Content-Type': 'text/plain' },
    }).catch(() => {
      /* the capture never disrupts the front door */
    });
  } catch {
    /* no fetch */
  }
}

export const capturePick = (chip: string): void => void beacon({ type: 'pick', chip, ...visitor() });

/** `picked` is the example last clicked, even if its words were then changed (the draft's chip is then null). */
export const captureCreate = (draft: DraftFields, picked: string | null): void =>
  void beacon({
    type: 'create',
    words: draft.prompt,
    chip: draft.chip,
    picked,
    look: draft.look?.id ?? null,
    lookFrom: draft.look?.from ?? null,
    ...visitor(),
  });

export const captureSignInShown = (): void => void beacon({ type: 'signin_shown' });
export const captureSignInDone = (): void => void beacon({ type: 'signin_done' });

/** Signed in, once the home screen has started the video from the draft: links what the landing recorded. */
export async function claimFrontDoor(words: string, projectId: string): Promise<void> {
  if (!(await captureOpen())) return;
  try {
    await api.post('/front-door/claim', { page: PAGE, words, projectId, ...visitor() });
  } catch {
    /* the capture never disrupts the start */
  }
}
